import { Star } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "@/api/client";
import { toastApiError } from "@/api/errors";
import type {
  Team,
  TeamAllowedModel,
  TeamAllowedModelsPut,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import {
  useCreateTeam,
  useUpdateTeam,
} from "@/features/teams/hooks/useTeams";
import { useTeamAllowedModels } from "@/features/teams/hooks/useTeamAllowedModels";
import { cn } from "@/lib/utils";

import { IconPicker } from "./IconPicker";

interface TeamFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: Team | null;
}

/** Paleta inicial -- usuario pode usar tambem o color picker abaixo. */
const COLOR_SWATCHES = [
  "#0ea5e9",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#64748b",
];

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface SelectedModel {
  llm_model_id: string;
  is_default: boolean;
}

export function TeamFormDialog({
  open,
  onOpenChange,
  team,
}: TeamFormDialogProps) {
  const { t } = useTranslation("common");
  const { t: tt } = useTranslation("teams");
  const create = useCreateTeam();
  const update = useUpdateTeam(team?.id ?? "");
  const llmModels = useLlmModels({ include_inactive: false });
  const existingAllowed = useTeamAllowedModels(team?.id ?? "");
  const isEdit = Boolean(team);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [color, setColor] = useState("");
  const [selectedModels, setSelectedModels] = useState<SelectedModel[]>([]);

  useEffect(() => {
    if (open && team) {
      setName(team.name);
      setSlug(team.slug);
      setDescription(team.description ?? "");
      setIcon(team.icon ?? "");
      setColor(team.color ?? "");
      setSlugTouched(true);
    } else if (open) {
      setName("");
      setSlug("");
      setDescription("");
      setIcon("");
      setColor("");
      setSlugTouched(false);
      setSelectedModels([]);
    }
  }, [open, team]);

  // Quando editando, pre-popula com os modelos ja permitidos do time.
  useEffect(() => {
    if (open && team && existingAllowed.data) {
      setSelectedModels(
        existingAllowed.data.map((m) => ({
          llm_model_id: m.llm_model_id,
          is_default: m.is_default,
        })),
      );
    }
  }, [open, team, existingAllowed.data]);

  // Auto-default: se ha apenas 1 selecionado, marca como default.
  useEffect(() => {
    if (selectedModels.length === 1 && !selectedModels[0]!.is_default) {
      setSelectedModels([{ ...selectedModels[0]!, is_default: true }]);
    }
  }, [selectedModels]);

  // Sugere slug automaticamente enquanto o user digita o nome,
  // a menos que ele edite o slug manualmente.
  const autoSlug = useMemo(() => slugify(name), [name]);
  useEffect(() => {
    if (!isEdit && !slugTouched) {
      setSlug(autoSlug);
    }
  }, [autoSlug, isEdit, slugTouched]);

  const selectedById = useMemo(
    () =>
      new Map<string, SelectedModel>(
        selectedModels.map((s) => [s.llm_model_id, s]),
      ),
    [selectedModels],
  );

  function toggleModel(modelId: string) {
    setSelectedModels((prev) => {
      const exists = prev.some((s) => s.llm_model_id === modelId);
      if (exists) {
        return prev.filter((s) => s.llm_model_id !== modelId);
      }
      return [
        ...prev,
        { llm_model_id: modelId, is_default: prev.length === 0 },
      ];
    });
  }

  function setAsDefault(modelId: string) {
    setSelectedModels((prev) =>
      prev.map((s) => ({ ...s, is_default: s.llm_model_id === modelId })),
    );
  }

  async function persistAllowedModels(teamId: string) {
    if (selectedModels.length === 0) return;
    const payload: TeamAllowedModelsPut = { items: selectedModels };
    await api.PUT<TeamAllowedModel[]>(
      `/teams/${teamId}/allowed-models`,
      payload,
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isEdit && team) {
        await update.mutateAsync({
          name,
          description: description || undefined,
          icon: icon || undefined,
          color: color || undefined,
        });
        await persistAllowedModels(team.id);
        toast.success(tt("form.toasts.updated"));
      } else {
        const created = await create.mutateAsync({
          name,
          slug: slug || undefined,
          description: description || undefined,
          icon: icon || undefined,
          color: color || undefined,
        });
        await persistAllowedModels(created.id);
        toast.success(tt("form.toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? tt("form.titleEdit") : tt("form.titleNew")}
          </DialogTitle>
          <DialogDescription>{tt("form.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">{tt("form.labels.name")}</Label>
            <Input
              id="team-name"
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {!isEdit ? (
            <div className="space-y-2">
              <Label htmlFor="team-slug">
                {tt("form.labels.slug")}{" "}
                <span className="text-ink-500">
                  {tt("form.labels.slugHint")}
                </span>
              </Label>
              <Input
                id="team-slug"
                pattern="[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                }}
                placeholder={tt("form.labels.slugPlaceholder")}
              />
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="team-desc">{tt("form.labels.description")}</Label>
            <Textarea
              id="team-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>
              {tt("form.labels.icon")}{" "}
              <span className="text-ink-500">
                {tt("form.labels.iconOptional")}
              </span>
            </Label>
            <IconPicker value={icon} onChange={setIcon} accent={color} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-color">
              {tt("form.labels.color")}{" "}
              <span className="text-ink-500">
                {tt("form.labels.colorOptional")}
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="team-color"
                type="color"
                value={color || "#0ea5e9"}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-16 cursor-pointer p-1"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#0ea5e9"
                maxLength={32}
              />
              <div className="flex gap-1">
                {COLOR_SWATCHES.map((swatch) => (
                  <button
                    key={swatch}
                    type="button"
                    aria-label={tt("form.labels.colorSwatchAria", {
                      color: swatch,
                    })}
                    onClick={() => setColor(swatch)}
                    className="h-6 w-6 rounded-full border border-line transition-transform hover:scale-110"
                    style={{ backgroundColor: swatch }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              {tt("form.labels.allowedModels")}{" "}
              <span className="text-ink-500">
                {tt("form.labels.allowedModelsHint")}
              </span>
            </Label>
            {llmModels.isLoading ? (
              <p className="text-xs text-ink-500">
                {tt("form.labels.loadingCatalog")}
              </p>
            ) : llmModels.data && llmModels.data.length === 0 ? (
              <p className="text-xs text-ink-500">
                {tt("form.labels.noModelsCatalog")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {llmModels.data?.map((m) => {
                  const sel = selectedById.get(m.id);
                  const isSelected = Boolean(sel);
                  const isDefault = sel?.is_default ?? false;
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors",
                        isSelected
                          ? "border-brand-300 bg-brand-50 text-brand-700"
                          : "border-line bg-surface text-ink-500 hover:border-brand-300 hover:text-brand-700",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleModel(m.id)}
                        className="font-semibold"
                      >
                        {m.display_name}
                      </button>
                      {isSelected && selectedModels.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => setAsDefault(m.id)}
                          aria-label={
                            isDefault
                              ? tt("form.labels.modelDefaultAria")
                              : tt("form.labels.makeDefaultAria")
                          }
                          title={
                            isDefault
                              ? tt("form.labels.modelDefaultAria")
                              : tt("form.labels.makeDefaultAria")
                          }
                          className={cn(
                            "inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors",
                            isDefault
                              ? "text-accent-500"
                              : "text-ink-400 hover:text-accent-500",
                          )}
                        >
                          <Star
                            className="h-3 w-3"
                            fill={isDefault ? "currentColor" : "none"}
                          />
                        </button>
                      ) : null}
                      {isSelected && selectedModels.length === 1 ? (
                        <span className="rounded-full bg-brand-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                          {tt("form.labels.default")}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
            {selectedModels.length > 1 ? (
              <p className="text-xs text-ink-500">
                {tt("form.labels.starHint")}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("actions.cancel")}
            </Button>
            <Button type="submit" disabled={pending || name.length < 2}>
              {isEdit ? t("actions.save") : t("actions.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
